import { useCallback } from 'react';

import { usePostHog } from 'posthog-js/react';

import {
  buildAnalyticsEvent,
  buildCopyEventData,
  type CopyContentType
} from '@/features/DataHub/modules/Export/services/ExportAnalyticsService';
import type { ExportSettingsType } from '@/features/DataHub/modules/Export/types/context.types';
import type { ExportOption } from '@/features/DataHub/modules/Export/types/exportConfig.types';
import { useAppSelector } from '@/hooks/redux-hooks';

const COPY_CONTENT_TYPE_MAP: Partial<Record<string, CopyContentType>> = {
  gx_api: 'api',
  curl: 'curl',
  python: 'python',
  power_query: 'powerquery',
  snowflake: 'snowflake'
};

const useExportAnalytics = (
  exportOption: ExportOption | null,
  exportSettings: ExportSettingsType
) => {
  const postHog = usePostHog();
  const symbols = useAppSelector(state => state.symbolSelector.symbols);

  const capturePosthogEvent = useCallback(() => {
    const { eventName, data } = buildAnalyticsEvent({ exportOption, symbols, exportSettings });
    postHog.capture(eventName, data);
  }, [exportOption, postHog, symbols, exportSettings]);

  const captureCopyEvent = useCallback(() => {
    const contentType = exportOption?.id ? COPY_CONTENT_TYPE_MAP[exportOption.id] : undefined;
    if (!contentType) return;
    const { eventName, data } = buildCopyEventData(
      contentType,
      exportOption,
      symbols,
      exportSettings
    );
    postHog.capture(eventName, data);
  }, [exportOption, postHog, symbols, exportSettings]);

  return { capturePosthogEvent, captureCopyEvent };
};

export default useExportAnalytics;
